import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "wouter";
import { nanoid } from "nanoid";
import type { SessionEventType } from "@shared/schema";

const SESSION_TOKEN_KEY = "openbox_session_token";
const SESSION_START_KEY = "openbox_session_start";
const SESSION_INIT_KEY = "openbox_session_initialized";
const PAGE_VIEW_TRACKED_KEY = "openbox_initial_pageview_tracked";
const BATCH_INTERVAL = 5000; // Send events every 5 seconds
const MAX_BATCH_SIZE = 50; // Max events per batch

interface TrackedEvent {
  eventType: SessionEventType;
  pagePath?: string;
  pageTitle?: string;
  elementTag?: string;
  elementId?: string;
  elementClass?: string;
  elementText?: string;
  elementTestId?: string;
  elementHref?: string;
  isError?: boolean;
  errorMessage?: string;
  errorStack?: string;
  errorCode?: string;
  metadata?: Record<string, any>;
  timestamp: string;
  pageLoadTime?: number;
}

interface SessionMeta {
  userAgent?: string;
  browserName?: string;
  browserVersion?: string;
  osName?: string;
  osVersion?: string;
  deviceType?: string;
  screenWidth?: number;
  screenHeight?: number;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
}

function getOrCreateSessionToken(): { token: string; isNew: boolean } {
  let token = sessionStorage.getItem(SESSION_TOKEN_KEY);
  const isNew = !token;
  if (!token) {
    token = nanoid(32);
    sessionStorage.setItem(SESSION_TOKEN_KEY, token);
    sessionStorage.setItem(SESSION_START_KEY, Date.now().toString());
    // Clear session-specific flags for new sessions
    sessionStorage.removeItem(SESSION_INIT_KEY);
    sessionStorage.removeItem(PAGE_VIEW_TRACKED_KEY);
  }
  return { token, isNew };
}

function getSessionMeta(): SessionMeta {
  const ua = navigator.userAgent;
  const url = new URL(window.location.href);
  
  const browserInfo = parseBrowserInfo(ua);
  const osInfo = parseOSInfo(ua);
  
  return {
    userAgent: ua,
    browserName: browserInfo.name,
    browserVersion: browserInfo.version,
    osName: osInfo.name,
    osVersion: osInfo.version,
    deviceType: getDeviceType(),
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    referrer: document.referrer || undefined,
    utmSource: url.searchParams.get("utm_source") || undefined,
    utmMedium: url.searchParams.get("utm_medium") || undefined,
    utmCampaign: url.searchParams.get("utm_campaign") || undefined,
    utmContent: url.searchParams.get("utm_content") || undefined,
    utmTerm: url.searchParams.get("utm_term") || undefined,
  };
}

function parseBrowserInfo(ua: string): { name: string; version: string } {
  const browsers = [
    { name: "Chrome", regex: /Chrome\/(\d+\.\d+)/ },
    { name: "Firefox", regex: /Firefox\/(\d+\.\d+)/ },
    { name: "Safari", regex: /Version\/(\d+\.\d+).*Safari/ },
    { name: "Edge", regex: /Edg\/(\d+\.\d+)/ },
    { name: "Opera", regex: /OPR\/(\d+\.\d+)/ },
  ];
  
  for (const browser of browsers) {
    const match = ua.match(browser.regex);
    if (match) {
      return { name: browser.name, version: match[1] };
    }
  }
  
  return { name: "Unknown", version: "0.0" };
}

function parseOSInfo(ua: string): { name: string; version: string } {
  if (/Windows NT 10/.test(ua)) return { name: "Windows", version: "10" };
  if (/Windows NT 6.3/.test(ua)) return { name: "Windows", version: "8.1" };
  if (/Windows NT 6.2/.test(ua)) return { name: "Windows", version: "8" };
  if (/Windows/.test(ua)) return { name: "Windows", version: "Unknown" };
  if (/Mac OS X (\d+[._]\d+)/.test(ua)) {
    const match = ua.match(/Mac OS X (\d+[._]\d+)/);
    return { name: "macOS", version: match ? match[1].replace("_", ".") : "Unknown" };
  }
  if (/Linux/.test(ua)) return { name: "Linux", version: "Unknown" };
  if (/Android (\d+\.\d+)/.test(ua)) {
    const match = ua.match(/Android (\d+\.\d+)/);
    return { name: "Android", version: match ? match[1] : "Unknown" };
  }
  if (/iPhone|iPad/.test(ua)) {
    const match = ua.match(/OS (\d+_\d+)/);
    return { name: "iOS", version: match ? match[1].replace("_", ".") : "Unknown" };
  }
  return { name: "Unknown", version: "0.0" };
}

function getDeviceType(): string {
  const ua = navigator.userAgent;
  if (/Tablet|iPad/i.test(ua)) return "tablet";
  if (/Mobile|Android|iPhone/i.test(ua)) return "mobile";
  return "desktop";
}

function getPageLoadTime(): number {
  const startTime = sessionStorage.getItem(SESSION_START_KEY);
  return startTime ? Date.now() - parseInt(startTime, 10) : 0;
}

function truncateText(text: string, maxLength = 100): string {
  if (!text) return "";
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
}

interface ClickRecord {
  timestamp: number;
  elementKey: string;
}

class SessionTracker {
  private sessionToken: string;
  private eventQueue: TrackedEvent[] = [];
  private batchInterval: ReturnType<typeof setInterval> | null = null;
  private isNewSession = false;
  private sessionMeta: SessionMeta | null = null;
  private clickHistory: ClickRecord[] = [];
  private rageClickThreshold = 3; // 3+ clicks = rage click
  private rageClickWindow = 1000; // 1 second window
  private rageClickCooldown = new Map<string, number>(); // Prevent duplicate rage click events
  private recentPageViews = new Map<string, number>(); // Deduplication for page views (path -> timestamp)
  private sessionInitialized = false;
  private pageViewDedupeWindowMs = 500; // Deduplication window in milliseconds

  constructor() {
    const { token, isNew } = getOrCreateSessionToken();
    this.sessionToken = token;
    this.isNewSession = isNew;
    this.sessionInitialized = sessionStorage.getItem(SESSION_INIT_KEY) === 'true';
    
    if (this.isNewSession) {
      this.sessionMeta = getSessionMeta();
      // Initialize session server-side immediately
      this.initializeServerSession();
      this.trackEvent({
        eventType: "SESSION_START",
        pagePath: window.location.pathname,
        pageTitle: document.title,
        timestamp: new Date().toISOString(),
      });
    }
    
    // Guarantee initial PAGE_VIEW is tracked
    this.trackInitialPageView();
  }
  
  private async initializeServerSession() {
    if (this.sessionInitialized) return;
    
    try {
      const response = await fetch("/api/sessions/init", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionToken: this.sessionToken,
          pagePath: window.location.pathname,
          pageTitle: document.title,
          sessionMeta: this.sessionMeta,
        }),
      });
      
      if (response.ok) {
        sessionStorage.setItem(SESSION_INIT_KEY, 'true');
        this.sessionInitialized = true;
      }
    } catch (error) {
      // Silently fail - session will be created on first event batch
      console.warn("[SessionTracker] Failed to initialize server session:", error);
    }
  }
  
  private trackInitialPageView() {
    // Check if initial page view was already tracked in this tab session
    const initialTracked = sessionStorage.getItem(PAGE_VIEW_TRACKED_KEY);
    if (initialTracked) return;
    
    // Track initial page view immediately
    this.trackEvent({
      eventType: "PAGE_VIEW",
      pagePath: window.location.pathname,
      pageTitle: document.title,
      timestamp: new Date().toISOString(),
      pageLoadTime: 0,
    });
    
    sessionStorage.setItem(PAGE_VIEW_TRACKED_KEY, 'true');
    this.recentPageViews.set(window.location.pathname, Date.now());
  }

  start() {
    if (!this.batchInterval) {
      this.batchInterval = setInterval(() => this.flush(), BATCH_INTERVAL);
    }
    
    document.addEventListener("click", this.handleClick);
    window.addEventListener("beforeunload", this.handleUnload);
    window.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("error", this.handleError);
    window.addEventListener("unhandledrejection", this.handlePromiseRejection);
    
    this.interceptConsole();
  }

  stop() {
    if (this.batchInterval) {
      clearInterval(this.batchInterval);
      this.batchInterval = null;
    }
    
    document.removeEventListener("click", this.handleClick);
    window.removeEventListener("beforeunload", this.handleUnload);
    window.removeEventListener("visibilitychange", this.handleVisibilityChange);
    window.removeEventListener("error", this.handleError);
    window.removeEventListener("unhandledrejection", this.handlePromiseRejection);
    
    this.flush();
  }

  private getElementKey(element: HTMLElement): string {
    const testId = element.getAttribute("data-testid");
    if (testId) return `testid:${testId}`;
    if (element.id) return `id:${element.id}`;
    const classList = element.className?.toString() || "";
    const path = this.getElementPath(element);
    return `path:${path}:${classList.slice(0, 50)}`;
  }

  private getElementPath(element: HTMLElement): string {
    const parts: string[] = [];
    let current: HTMLElement | null = element;
    let depth = 0;
    while (current && depth < 3) {
      parts.unshift(current.tagName.toLowerCase());
      current = current.parentElement;
      depth++;
    }
    return parts.join(">");
  }

  private checkRageClick(elementKey: string): boolean {
    const now = Date.now();
    
    this.clickHistory.push({ timestamp: now, elementKey });
    this.clickHistory = this.clickHistory.filter(c => now - c.timestamp < this.rageClickWindow);
    
    const recentClicks = this.clickHistory.filter(c => c.elementKey === elementKey);
    
    if (recentClicks.length >= this.rageClickThreshold) {
      const lastRageClick = this.rageClickCooldown.get(elementKey);
      if (!lastRageClick || now - lastRageClick > 3000) {
        this.rageClickCooldown.set(elementKey, now);
        return true;
      }
    }
    return false;
  }

  private handleClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target) return;
    
    const clickableElement = target.closest("button, a, [role=button], input[type=submit], [data-testid]");
    const element = (clickableElement || target) as HTMLElement;
    
    const elementKey = this.getElementKey(element);
    const isRageClick = this.checkRageClick(elementKey);
    
    this.trackEvent({
      eventType: "CLICK",
      pagePath: window.location.pathname,
      pageTitle: document.title,
      elementTag: element.tagName.toLowerCase(),
      elementId: element.id || undefined,
      elementClass: element.className ? truncateText(element.className.toString(), 200) : undefined,
      elementText: truncateText(element.textContent || "", 100),
      elementTestId: element.getAttribute("data-testid") || undefined,
      elementHref: (element as HTMLAnchorElement).href || undefined,
      timestamp: new Date().toISOString(),
      pageLoadTime: getPageLoadTime(),
    });
    
    if (isRageClick) {
      this.trackEvent({
        eventType: "RAGE_CLICK",
        pagePath: window.location.pathname,
        pageTitle: document.title,
        elementTag: element.tagName.toLowerCase(),
        elementId: element.id || undefined,
        elementClass: element.className ? truncateText(element.className.toString(), 200) : undefined,
        elementText: truncateText(element.textContent || "", 100),
        elementTestId: element.getAttribute("data-testid") || undefined,
        elementHref: (element as HTMLAnchorElement).href || undefined,
        isError: true,
        errorMessage: `Rage click detected: ${this.clickHistory.filter(c => c.elementKey === elementKey).length} rapid clicks`,
        metadata: { 
          clickCount: this.clickHistory.filter(c => c.elementKey === elementKey).length,
          elementKey 
        },
        timestamp: new Date().toISOString(),
        pageLoadTime: getPageLoadTime(),
      });
    }
  };

  private handleUnload = () => {
    this.trackEvent({
      eventType: "SESSION_END",
      pagePath: window.location.pathname,
      timestamp: new Date().toISOString(),
      pageLoadTime: getPageLoadTime(),
    });
    this.flush(true); // Sync flush on unload
  };

  private handleVisibilityChange = () => {
    this.trackEvent({
      eventType: "VISIBILITY_CHANGE",
      pagePath: window.location.pathname,
      metadata: { hidden: document.hidden },
      timestamp: new Date().toISOString(),
      pageLoadTime: getPageLoadTime(),
    });
    
    // Flush events when page becomes hidden (user switches tabs/minimizes)
    if (document.hidden) {
      this.flush(true); // Sync flush to ensure events are sent before tab is suspended
    }
  };

  private isExtensionNoise(message: string | undefined | null): boolean {
    if (!message) return false;
    const m = String(message).toLowerCase();
    // Microsoft Outlook SafeLink / Edge read-aloud injected helper. Not our code.
    return m.includes('object not found matching id');
  }

  private handleError = (e: ErrorEvent) => {
    if (this.isExtensionNoise(e.message)) return;
    this.trackEvent({
      eventType: "CONSOLE_ERROR",
      pagePath: window.location.pathname,
      isError: true,
      errorMessage: e.message,
      errorStack: e.error?.stack,
      metadata: {
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno,
      },
      timestamp: new Date().toISOString(),
      pageLoadTime: getPageLoadTime(),
    });
  };

  private handlePromiseRejection = (e: PromiseRejectionEvent) => {
    const msg = e.reason?.message || String(e.reason);
    if (this.isExtensionNoise(msg)) return;
    this.trackEvent({
      eventType: "CONSOLE_ERROR",
      pagePath: window.location.pathname,
      isError: true,
      errorMessage: msg,
      errorStack: e.reason?.stack,
      metadata: { type: "unhandledrejection" },
      timestamp: new Date().toISOString(),
      pageLoadTime: getPageLoadTime(),
    });
  };

  private interceptConsole() {
    const originalError = console.error;
    const originalWarn = console.warn;
    
    console.error = (...args: any[]) => {
      this.trackEvent({
        eventType: "CONSOLE_ERROR",
        pagePath: window.location.pathname,
        isError: true,
        errorMessage: args.map(a => String(a)).join(" "),
        timestamp: new Date().toISOString(),
        pageLoadTime: getPageLoadTime(),
      });
      originalError.apply(console, args);
    };
    
    console.warn = (...args: any[]) => {
      this.trackEvent({
        eventType: "CONSOLE_WARN",
        pagePath: window.location.pathname,
        isError: false,
        errorMessage: args.map(a => String(a)).join(" "),
        timestamp: new Date().toISOString(),
        pageLoadTime: getPageLoadTime(),
      });
      originalWarn.apply(console, args);
    };
  }

  trackEvent(event: TrackedEvent) {
    this.eventQueue.push(event);
    
    if (this.eventQueue.length >= MAX_BATCH_SIZE) {
      this.flush();
    }
  }

  trackPageView(path: string, title?: string) {
    // Deduplication: skip if this page was viewed very recently (within dedupeWindowMs)
    // This prevents duplicate tracking from multiple sources (wouter, initial load, etc.)
    const now = Date.now();
    const lastViewTime = this.recentPageViews.get(path);
    
    if (lastViewTime && (now - lastViewTime) < this.pageViewDedupeWindowMs) {
      // Skip duplicate - page was viewed within the dedup window
      return;
    }
    
    // Record this view time (will be overwritten on next visit, auto-expiring old entries)
    this.recentPageViews.set(path, now);
    
    // Clean up old entries periodically (keep map small)
    if (this.recentPageViews.size > 20) {
      const expiryTime = now - this.pageViewDedupeWindowMs;
      const entries = Array.from(this.recentPageViews.entries());
      for (const [key, time] of entries) {
        if (time < expiryTime) {
          this.recentPageViews.delete(key);
        }
      }
    }
    
    this.trackEvent({
      eventType: "PAGE_VIEW",
      pagePath: path,
      pageTitle: title || document.title,
      timestamp: new Date().toISOString(),
      pageLoadTime: getPageLoadTime(),
    });
  }

  trackApiError(url: string, status: number, message: string) {
    this.trackEvent({
      eventType: "API_ERROR",
      pagePath: window.location.pathname,
      isError: true,
      errorMessage: message,
      errorCode: String(status),
      metadata: { url },
      timestamp: new Date().toISOString(),
      pageLoadTime: getPageLoadTime(),
    });
  }

  trackToast(variant: "default" | "destructive", title?: string, description?: string) {
    const isError = variant === "destructive";
    this.trackEvent({
      eventType: isError ? "TOAST_ERROR" : "TOAST_WARNING",
      pagePath: window.location.pathname,
      isError,
      errorMessage: isError ? `${title || ""}: ${description || ""}`.trim() : undefined,
      metadata: { title, description, variant },
      timestamp: new Date().toISOString(),
      pageLoadTime: getPageLoadTime(),
    });
  }

  trackCartAction(action: "add" | "remove", modelNumber: string, quantity: number, metadata?: Record<string, any>) {
    this.trackEvent({
      eventType: action === "add" ? "CART_ADD" : "CART_REMOVE",
      pagePath: window.location.pathname,
      metadata: { modelNumber, quantity, ...metadata },
      timestamp: new Date().toISOString(),
      pageLoadTime: getPageLoadTime(),
    });
  }

  trackCheckout(stage: "start" | "complete" | "error", orderId?: string, errorMessage?: string) {
    const eventTypes = {
      start: "CHECKOUT_START" as const,
      complete: "CHECKOUT_COMPLETE" as const,
      error: "CHECKOUT_ERROR" as const,
    };
    
    this.trackEvent({
      eventType: eventTypes[stage],
      pagePath: window.location.pathname,
      isError: stage === "error",
      errorMessage: errorMessage,
      metadata: { orderId },
      timestamp: new Date().toISOString(),
      pageLoadTime: getPageLoadTime(),
    });
  }

  trackLogin(userId?: string, email?: string) {
    this.trackEvent({
      eventType: "LOGIN",
      pagePath: window.location.pathname,
      metadata: { userId, email },
      timestamp: new Date().toISOString(),
      pageLoadTime: getPageLoadTime(),
    });
  }

  trackLogout() {
    this.trackEvent({
      eventType: "LOGOUT",
      pagePath: window.location.pathname,
      timestamp: new Date().toISOString(),
      pageLoadTime: getPageLoadTime(),
    });
  }

  private async flush(sync = false) {
    if (this.eventQueue.length === 0) return;
    
    const events = [...this.eventQueue];
    this.eventQueue = [];
    
    const payload = {
      sessionToken: this.sessionToken,
      events,
      sessionMeta: this.sessionMeta,
    };
    
    if (this.sessionMeta) {
      this.sessionMeta = null;
    }
    
    try {
      if (sync && navigator.sendBeacon) {
        // Use Blob with content-type to ensure JSON parsing works on the server
        const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
        navigator.sendBeacon("/api/session-events", blob);
      } else {
        await fetch("/api/session-events", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          keepalive: true,
        });
      }
    } catch (error) {
      this.eventQueue.unshift(...events);
    }
  }

  getSessionToken(): string {
    return this.sessionToken;
  }
}

let trackerInstance: SessionTracker | null = null;

export function getSessionTracker(): SessionTracker {
  if (!trackerInstance) {
    trackerInstance = new SessionTracker();
  }
  return trackerInstance;
}

export function useSessionTracker() {
  const tracker = useRef<SessionTracker | null>(null);
  const [location] = useLocation();
  const lastPath = useRef<string>("");

  useEffect(() => {
    tracker.current = getSessionTracker();
    tracker.current.start();

    return () => {
      tracker.current?.stop();
    };
  }, []);

  useEffect(() => {
    if (location !== lastPath.current) {
      lastPath.current = location;
      tracker.current?.trackPageView(location);
    }
  }, [location]);

  const trackApiError = useCallback((url: string, status: number, message: string) => {
    tracker.current?.trackApiError(url, status, message);
  }, []);

  const trackToast = useCallback((variant: "default" | "destructive", title?: string, description?: string) => {
    tracker.current?.trackToast(variant, title, description);
  }, []);

  const trackCartAction = useCallback((action: "add" | "remove", modelNumber: string, quantity: number, metadata?: Record<string, any>) => {
    tracker.current?.trackCartAction(action, modelNumber, quantity, metadata);
  }, []);

  const trackCheckout = useCallback((stage: "start" | "complete" | "error", orderId?: string, errorMessage?: string) => {
    tracker.current?.trackCheckout(stage, orderId, errorMessage);
  }, []);

  const trackLogin = useCallback((userId?: string, email?: string) => {
    tracker.current?.trackLogin(userId, email);
  }, []);

  const trackLogout = useCallback(() => {
    tracker.current?.trackLogout();
  }, []);

  return {
    trackApiError,
    trackToast,
    trackCartAction,
    trackCheckout,
    trackLogin,
    trackLogout,
    getSessionToken: () => tracker.current?.getSessionToken(),
  };
}
